import { COLLECTIONS } from '../hooks/useFirebase';

export class ScheduledTaskService {
    constructor(moveTask) {
        this.moveTask = moveTask;
        this.scheduledTasks = new Map();
        this.timeouts = new Map();
    }

    // Calculate delay until task should be activated
    calculateDelay(scheduledFor) {
        const now = new Date().getTime();
        const scheduledTime = new Date(scheduledFor).getTime();
        return Math.max(0, scheduledTime - now);
    }

    // Schedule a single task
    scheduleTask(task) {
        if (!task.id || !task.scheduledFor) {
            console.warn('Invalid task format:', task);
            return;
        }

        // Clear existing timeout if any
        this.clearTaskSchedule(task.id);

        const delay = this.calculateDelay(task.scheduledFor);

        // If the scheduled time has already passed, activate immediately
        if (delay === 0) {
            this.activateTask(task);
            return;
        }

        // Schedule the task activation
        const timeoutId = setTimeout(() => {
            this.activateTask(task);
        }, delay);

        // Store the task and its timeout
        this.scheduledTasks.set(task.id, task);
        this.timeouts.set(task.id, timeoutId);

        // Log scheduling for debugging
        console.log(`Task ${task.id} scheduled for ${new Date(task.scheduledFor).toLocaleString()}`);
    }

    // Activate a task by moving it to the active collection
    async activateTask(task) {
        try {
            // Remove scheduling-specific fields and add activation time
            const updates = {
                scheduledFor: null,
                status: 'active',
                activatedAt: new Date().toISOString()
            };

            // Move task from scheduled to active collection
            await this.moveTask(
                task.id,
                COLLECTIONS.SCHEDULED,
                COLLECTIONS.ACTIVE,
                updates
            );

            // Clean up task tracking
            this.clearTaskSchedule(task.id);
            console.log(`Task ${task.id} activated at ${new Date().toLocaleString()}`);
        } catch (error) {
            console.error('Error activating task:', error);
        }
    }

    // Clear scheduling for a task
    clearTaskSchedule(taskId) {
        const timeoutId = this.timeouts.get(taskId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeouts.delete(taskId);
        }
        this.scheduledTasks.delete(taskId);
    }

    // Initialize service with existing scheduled tasks
    initializeScheduledTasks(tasks) {
        // Clear all existing schedules
        this.clearAllSchedules();

        // Schedule all provided tasks
        tasks.forEach(task => {
            if (task.scheduledFor) {
                this.scheduleTask(task);
            }
        });
    }

    // Clear all scheduled tasks
    clearAllSchedules() {
        this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.timeouts.clear();
        this.scheduledTasks.clear();
    }

    // Handle page visibility changes
    handleVisibilityChange() {
        if (document.hidden) {
            // Store scheduled times before clearing
            const scheduledTasks = Array.from(this.scheduledTasks.values());
            localStorage.setItem('scheduledTasks', JSON.stringify(scheduledTasks));
            this.clearAllSchedules();
        } else {
            // Reload and reschedule tasks
            try {
                const storedTasks = localStorage.getItem('scheduledTasks');
                if (storedTasks) {
                    const tasks = JSON.parse(storedTasks);
                    tasks.forEach(task => {
                        if (this.calculateDelay(task.scheduledFor) > 0) {
                            this.scheduleTask(task);
                        } else {
                            this.activateTask(task);
                        }
                    });
                }
            } catch (error) {
                console.error('Error reloading scheduled tasks:', error);
            }
        }
    }

    // Clean up resources
    cleanup() {
        this.clearAllSchedules();
        localStorage.removeItem('scheduledTasks');
    }
}